import {
  buildTenantPath,
  findEntityModulePath,
  type ResolvedModule,
} from '@web-config-app/core';

/**
 * Returns true when the pathname is a pre-module-system deep link
 * (e.g. `/domains/health/entities/activities/environments/draft/list`).
 *
 * Only `/domains/` paths need preservation — `/admin/` was a legacy
 * navigation concept that no longer has external bookmarks.
 */
export const isLegacyDomainPath = (pathname: string): boolean =>
  pathname.startsWith('/domains/') || pathname === '/domains';

/**
 * Builds a redirect destination for a legacy domain URL, or returns null
 * if the URL can't be mapped to the new module structure.
 *
 * Old: /tenants/:tenantId/domains/:domainId/entities/:entityId/environments/:envKey/...
 * New: /tenants/:tenantId/:tabId/:moduleId/environments/:envKey/...
 */
export const buildLegacyDomainRedirect = (
  pathname: string,
  search: string,
  tenantId: string | undefined,
  entityConfigs: Record<string, { type: string }>,
  modules: ResolvedModule[],
): string | null => {
  const segments = pathname.split('/');
  const domainsIdx = segments.indexOf('domains');
  if (domainsIdx === -1) return null;

  const afterDomains = segments.slice(domainsIdx + 1);
  const entitiesIdx = afterDomains.indexOf('entities');
  const environmentsIdx = afterDomains.indexOf('environments');

  // Domain-only URL (e.g. /domains/journey) — redirect to tenant root
  if (entitiesIdx === -1 || environmentsIdx === -1) {
    return buildTenantPath(tenantId);
  }

  const entityId = afterDomains[entitiesIdx + 1];
  const envKey = afterDomains[environmentsIdx + 1];
  const trailingPath = afterDomains.slice(environmentsIdx + 2).join('/');

  if (!entityId || !envKey) return buildTenantPath(tenantId);

  const entityConfig = entityConfigs[entityId];
  // Entity was removed, renamed, or is not accessible to the current
  // user. Fall back to the tenant landing page rather than showing a
  // generic error so old bookmarks stay useful.
  if (!entityConfig) return buildTenantPath(tenantId);

  const modulePath = findEntityModulePath(
    modules,
    entityConfig.type,
    entityConfigs,
  );
  if (!modulePath) return null;

  let moduleSegment: string;
  if (modulePath.childModuleId) {
    moduleSegment = `${modulePath.tabId}/${modulePath.moduleId}/${modulePath.childModuleId}`;
  } else if (modulePath.moduleId) {
    moduleSegment = `${modulePath.tabId}/${modulePath.moduleId}`;
  } else {
    moduleSegment = modulePath.tabId;
  }

  return buildTenantPath(
    tenantId,
    `${moduleSegment}/environments/${envKey}/${trailingPath}${search}`,
  );
};
